import * as Sentry from "@sentry/svelte";

// Shared loader for the microfrontends this shell mounts at runtime (Event, Cut and
// Content Manager), each imported from its own container.

/** How long to wait for a custom element to register after its bundle loaded. */
const CUSTOM_ELEMENT_TIMEOUT_MS = 15000;

export class MicrofrontendLoadError extends Error {
  constructor(message, url, cause) {
    super(message);
    this.name = "MicrofrontendLoadError";
    this.url = url;
    this.cause = cause;
  }
}

/**
 * Read the bundle's current ETag so it can be used as a cache-buster. A failing
 * probe only costs cache-busting, so the caller still gets to try the import.
 */
async function readEtag(url) {
  try {
    const response = await fetch(`${url}?timestamp=${Date.now()}`, {
      method: "HEAD",
    });
    return response.headers.get("etag");
  } catch {
    return null;
  }
}

/**
 * @param {string} url absolute path of the bundle, e.g. "/cut-manager-frontend/cut-manager.es.js"
 * @param {string} name microfrontend identifier, used for error reporting
 * @returns {Promise<object>} the imported module
 * @throws {MicrofrontendLoadError} if the bundle cannot be loaded or evaluated
 */
export async function loadMicrofrontendModule(url, name) {
  const etag = await readEtag(url);
  const moduleUrl = `${url}?etag=${etag}`;

  try {
    return await import(/* @vite-ignore */ moduleUrl);
  } catch (cause) {
    throw reportLoadFailure(
      new MicrofrontendLoadError(
        `Failed to load the ${name} microfrontend from ${moduleUrl}`,
        url,
        cause,
      ),
      name,
    );
  }
}

/**
 * Wait until a bundle registers its custom element. The Content Manager calls
 * customElements.define() inside an async IIFE that the importer cannot await, so a
 * resolved import() does not mean the element exists yet.
 */
export async function waitForCustomElement(
  tagName,
  name,
  timeoutMs = CUSTOM_ELEMENT_TIMEOUT_MS,
) {
  let timer;
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new MicrofrontendLoadError(
            `The ${name} microfrontend loaded but did not register <${tagName}> within ${timeoutMs}ms`,
            tagName,
          ),
        ),
      timeoutMs,
    );
  });

  try {
    await Promise.race([customElements.whenDefined(tagName), timeout]);
  } catch (error) {
    throw reportLoadFailure(error, name);
  } finally {
    clearTimeout(timer);
  }
}

/** Reports to Sentry and returns the error so callers can rethrow it. */
export function reportLoadFailure(error, name) {
  console.error(error);
  Sentry.captureException(error, { tags: { microfrontend: name } });
  return error;
}
